import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createPaymentLink, clearPaymentLink } from '../../actions/paymentLink';
import {
    Card,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Spinner,
    Badge,
    InputGroup
} from 'react-bootstrap';
import {
    Link2,
    Copy,
    CheckCircle,
    Mail,
    MessageSquare,
    Calendar,
    Globe,
    Bell,
    DollarSign,
    IndianRupee,
    FileText,
    ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PaymentLink = ({ createPaymentLink, clearPaymentLink, paymentLink, loading, error }) => {
    const [formData, setFormData] = useState({
        orderId: '',
        notifyEmail: false,
        notifySms: false,
        acceptPartial: false,
        firstMinPartialAmount: '',
        expireBy: '',
        callbackUrl: '',
        callbackMethod: 'get',
        reminderEnable: false,
        notes: {}
    });
    const [noteKey, setNoteKey] = useState('');
    const [noteValue, setNoteValue] = useState('');
    const [copied, setCopied] = useState(false);
    const [localError, setLocalError] = useState('');

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
        setLocalError('');
    };

    const handleAddNote = () => {
        if (noteKey && noteValue) {
            setFormData({
                ...formData,
                notes: {
                    ...formData.notes,
                    [noteKey]: noteValue
                }
            });
            setNoteKey('');
            setNoteValue('');
        }
    };

    const handleRemoveNote = (key) => {
        const newNotes = { ...formData.notes };
        delete newNotes[key];
        setFormData({
            ...formData,
            notes: newNotes
        });
    };

    const validate = () => {
        if (!formData.orderId.trim()) {
            setLocalError('Order ID is required.');
            return false;
        }
        if (formData.acceptPartial && (!formData.firstMinPartialAmount || Number(formData.firstMinPartialAmount) <= 0)) {
            setLocalError('Minimum partial payment amount is required when partial payments are enabled.');
            return false;
        }
        if (formData.callbackUrl && formData.callbackMethod !== 'get') {
            setLocalError('Callback method must be "get" when callback URL is provided.');
            return false;
        }
        setLocalError('');
        return true;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            orderId: formData.orderId.trim()
        };

        // Add optional fields only if they have values
        if (formData.notifyEmail) payload.notifyEmail = formData.notifyEmail;
        if (formData.notifySms) payload.notifySms = formData.notifySms;
        if (formData.acceptPartial) {
            payload.acceptPartial = formData.acceptPartial;
            if (formData.firstMinPartialAmount) {
                payload.firstMinPartialAmount = parseFloat(formData.firstMinPartialAmount);
            }
        }
        if (formData.expireBy) payload.expireBy = formData.expireBy;
        if (formData.callbackUrl) {
            payload.callbackUrl = formData.callbackUrl;
            payload.callbackMethod = formData.callbackMethod;
        }
        if (formData.reminderEnable) payload.reminderEnable = formData.reminderEnable;
        if (Object.keys(formData.notes).length > 0) payload.notes = formData.notes;

        try {
            await createPaymentLink(payload);
        } catch (err) {
            // Error handled by action
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            created: { variant: 'info', label: 'Created' },
            issued: { variant: 'primary', label: 'Issued' },
            paid: { variant: 'success', label: 'Paid' },
            partially_paid: { variant: 'warning', label: 'Partially Paid' },
            cancelled: { variant: 'danger', label: 'Cancelled' },
            expired: { variant: 'secondary', label: 'Expired' }
        };
        const config = statusConfig[status?.toLowerCase()] || { variant: 'secondary', label: status || 'Unknown' };
        return <Badge bg={config.variant}>{config.label}</Badge>;
    };

    const handleReset = () => {
        clearPaymentLink();
        setFormData({
            orderId: '',
            notifyEmail: false,
            notifySms: false,
            acceptPartial: false,
            firstMinPartialAmount: '',
            expireBy: '',
            callbackUrl: '',
            callbackMethod: 'get',
            reminderEnable: false,
            notes: {}
        });
        setNoteKey('');
        setNoteValue('');
        setLocalError('');
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div className="d-flex align-items-center">
                                <Link
                                    to="/inventory/v1.2/payment-links"
                                    className="btn btn-light rounded-circle me-3 d-flex align-items-center justify-content-center"
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        border: '1px solid #E9ECEF',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <ArrowLeft size={18} className="text-dark" />
                                </Link>
                                <div>
                                    <h1 className="h3 mb-2 text-primary fw-bold">
                                        <Link2 size={32} className="me-3" />
                                        Payment Link
                                    </h1>
                                    <p className="text-muted mb-0">
                                        Create Razorpay payment links for orders
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Success Message with Payment Link */}
                {paymentLink && (
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card className="shadow-sm border-0 bg-success text-white">
                                <Card.Body>
                                    <div className="d-flex align-items-center mb-3">
                                        <CheckCircle size={24} className="me-2" />
                                        <h5 className="mb-0">Payment Link Created Successfully!</h5>
                                    </div>
                                    <div className="bg-white text-dark p-3 rounded mb-3">
                                        <div className="mb-2">
                                            <strong>Payment Link URL:</strong>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <InputGroup>
                                                <Form.Control
                                                    type="text"
                                                    value={paymentLink.paymentLinkUrl || ''}
                                                    readOnly
                                                    className="bg-light"
                                                />
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={() => copyToClipboard(paymentLink.paymentLinkUrl)}
                                                >
                                                    {copied ? (
                                                        <>
                                                            <CheckCircle size={16} className="me-2" />
                                                            Copied!
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy size={16} className="me-2" />
                                                            Copy
                                                        </>
                                                    )}
                                                </Button>
                                            </InputGroup>
                                        </div>
                                        <div className="mt-3 d-flex gap-3 flex-wrap">
                                            <div>
                                                <strong>Status:</strong> {getStatusBadge(paymentLink.status)}
                                            </div>
                                            <div>
                                                <strong>Amount:</strong> ₹{paymentLink.amount?.toLocaleString() || '0.00'}
                                            </div>
                                            <div>
                                                <strong>Payment Link ID:</strong> <code>{paymentLink.paymentLink}</code>
                                            </div>
                                        </div>
                                        {paymentLink.paymentLinkUrl && (
                                            <div className="mt-3">
                                                <Button
                                                    variant="primary"
                                                    href={paymentLink.paymentLinkUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Open Payment Link
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    <Button variant="light" onClick={handleReset}>
                                        Create Another Payment Link
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Error Alerts */}
                {(error || localError) && (
                    <Row className="mb-4">
                        <Col md={12}>
                            <Alert variant="danger" dismissible onClose={() => { setLocalError(''); }}>
                                {error || localError}
                            </Alert>
                        </Col>
                    </Row>
                )}

                {/* Form */}
                <Row>
                    <Col md={8} lg={6}>
                        <Card className="shadow-sm border-0">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Link2 size={18} className="me-2" />
                                    Create Payment Link
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Form onSubmit={onSubmit}>
                                    {/* Order ID */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">
                                            Order ID <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="orderId"
                                            placeholder="Enter Order ID"
                                            value={formData.orderId}
                                            onChange={onChange}
                                            disabled={loading || !!paymentLink}
                                            required
                                        />
                                        <Form.Text className="text-muted">
                                            The order ID for which to create the payment link
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Notifications */}
                                    <div className="mb-3">
                                        <Form.Label className="fw-bold mb-2">Notifications</Form.Label>
                                        <div>
                                            <Form.Check
                                                type="checkbox"
                                                id="notifyEmail"
                                                name="notifyEmail"
                                                label={
                                                    <span>
                                                        <Mail size={14} className="me-1" />
                                                        Send Email Notification
                                                    </span>
                                                }
                                                checked={formData.notifyEmail}
                                                onChange={onChange}
                                                disabled={loading || !!paymentLink}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                id="notifySms"
                                                name="notifySms"
                                                label={
                                                    <span>
                                                        <MessageSquare size={14} className="me-1" />
                                                        Send SMS Notification
                                                    </span>
                                                }
                                                checked={formData.notifySms}
                                                onChange={onChange}
                                                disabled={loading || !!paymentLink}
                                            />
                                        </div>
                                    </div>

                                    {/* Partial Payments */}
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="acceptPartial"
                                            name="acceptPartial"
                                            label={
                                                <span>
                                                    <IndianRupee size={14} className="me-1" />
                                                    Allow Partial Payments
                                                </span>
                                            }
                                            checked={formData.acceptPartial}
                                            onChange={onChange}
                                            disabled={loading || !!paymentLink}
                                        />
                                        {formData.acceptPartial && (
                                            <div className="mt-2">
                                                <Form.Control
                                                    type="number"
                                                    name="firstMinPartialAmount"
                                                    placeholder="Minimum partial payment amount (₹)"
                                                    value={formData.firstMinPartialAmount}
                                                    onChange={onChange}
                                                    disabled={loading || !!paymentLink}
                                                    min="1"
                                                    step="0.01"
                                                />
                                                <Form.Text className="text-muted">
                                                    Minimum amount for first partial payment
                                                </Form.Text>
                                            </div>
                                        )}
                                    </Form.Group>

                                    {/* Expiry Date */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">
                                            <Calendar size={16} className="me-2 text-info" />
                                            Expiry Date
                                        </Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            name="expireBy"
                                            value={formData.expireBy}
                                            onChange={onChange}
                                            disabled={loading || !!paymentLink}
                                        />
                                        <Form.Text className="text-muted">
                                            Optional expiry date for the payment link
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Callback URL */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold">
                                            <Globe size={16} className="me-2 text-success" />
                                            Callback URL
                                        </Form.Label>
                                        <Form.Control
                                            type="url"
                                            name="callbackUrl"
                                            placeholder="https://yourapp.com/payment-success"
                                            value={formData.callbackUrl}
                                            onChange={onChange}
                                            disabled={loading || !!paymentLink}
                                        />
                                        {formData.callbackUrl && (
                                            <div className="mt-2">
                                                <Form.Select
                                                    name="callbackMethod"
                                                    value={formData.callbackMethod}
                                                    onChange={onChange}
                                                    disabled={loading || !!paymentLink}
                                                >
                                                    <option value="get">GET</option>
                                                </Form.Select>
                                                <Form.Text className="text-muted">
                                                    Callback method must be "get"
                                                </Form.Text>
                                            </div>
                                        )}
                                        <Form.Text className="text-muted">
                                            Redirect URL after payment completion
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Reminder */}
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            id="reminderEnable"
                                            name="reminderEnable"
                                            label={
                                                <span>
                                                    <Bell size={14} className="me-1" />
                                                    Enable Payment Reminders
                                                </span>
                                            }
                                            checked={formData.reminderEnable}
                                            onChange={onChange}
                                            disabled={loading || !!paymentLink}
                                        />
                                    </Form.Group>

                                    {/* Notes */}
                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-bold">
                                            <FileText size={16} className="me-2 text-warning" />
                                            Additional Notes
                                        </Form.Label>
                                        <div className="d-flex gap-2 mb-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Note Key"
                                                value={noteKey}
                                                onChange={(e) => setNoteKey(e.target.value)}
                                                disabled={loading || !!paymentLink}
                                            />
                                            <Form.Control
                                                type="text"
                                                placeholder="Note Value"
                                                value={noteValue}
                                                onChange={(e) => setNoteValue(e.target.value)}
                                                disabled={loading || !!paymentLink}
                                            />
                                            <Button
                                                variant="outline-primary"
                                                onClick={handleAddNote}
                                                disabled={loading || !!paymentLink || !noteKey || !noteValue}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                        {Object.keys(formData.notes).length > 0 && (
                                            <div className="mt-2">
                                                {Object.entries(formData.notes).map(([key, value]) => (
                                                    <Badge key={key} bg="info" className="me-2 mb-2 d-inline-flex align-items-center">
                                                        {key}: {value}
                                                        <button
                                                            type="button"
                                                            className="btn-close btn-close-white ms-2"
                                                            onClick={() => handleRemoveNote(key)}
                                                            disabled={loading || !!paymentLink}
                                                            style={{ fontSize: '0.7rem' }}
                                                        />
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                        <Form.Text className="text-muted">
                                            Add key-value pairs for additional information (max 15 pairs, 256 chars each)
                                        </Form.Text>
                                    </Form.Group>

                                    {/* Submit Button */}
                                    <div className="d-flex justify-content-end gap-2">
                                        {paymentLink && (
                                            <Button
                                                variant="outline-secondary"
                                                onClick={handleReset}
                                                disabled={loading}
                                            >
                                                Reset
                                            </Button>
                                        )}
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={loading || !!paymentLink}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Creating...
                                                </>
                                            ) : (
                                                <>
                                                    <Link2 size={16} className="me-2" />
                                                    Create Payment Link
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Fragment>
    );
};

PaymentLink.propTypes = {
    createPaymentLink: PropTypes.func.isRequired,
    clearPaymentLink: PropTypes.func.isRequired,
    paymentLink: PropTypes.object,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string
};

const mapStateToProps = (state) => ({
    paymentLink: state.paymentLink.paymentLink,
    loading: state.paymentLink.loading,
    error: state.paymentLink.error
});

export default connect(mapStateToProps, { createPaymentLink, clearPaymentLink })(PaymentLink);

